import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarRange, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BulkProgramPreview from "@/components/BulkProgramPreview";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const CHUNK = 8;

export default function BulkProgramDialog({ onDone, groups = [] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ group_id: "", start_date: "", end_date: "", exclusions: "", objective: "", intensity_profile: "progressivo" });
  const [days, setDays] = useState(DAYS.map((_, i) => ({ weekday: i, on: false, time: "16:00", duration: 60 })));
  const [calendar, setCalendar] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [chunks, setChunks] = useState([]); // {status: pending|ok|error, summary}
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const reset = () => { setCalendar(null); setSessions(null); setChunks([]); setProgress(""); };
  const groupName = groups.find((g) => g.id === form.group_id)?.name || "";

  const computeCalendar = async () => {
    const weekdays = days.filter((d) => d.on).map(({ weekday, time, duration }) => ({ weekday, time, duration: parseInt(duration) || 60 }));
    if (!form.group_id) return toast.error("Seleziona un gruppo");
    if (!form.start_date || !form.end_date) return toast.error("Date inizio/fine richieste");
    if (weekdays.length === 0) return toast.error("Seleziona almeno un giorno");
    setBusy(true);
    try {
      const { data } = await api.post("/trainings/bulk-plan/calendar", { group_id: form.group_id, start_date: form.start_date, end_date: form.end_date, weekdays, exclusions: form.exclusions });
      setCalendar(data);
      setSessions(data.slots.map((s) => ({ ...s, keep: !s.existing_id, objective: null, intensity: "media", blocks: [] })));
      setChunks(Array.from({ length: Math.ceil(data.slots.length / CHUNK) }, () => ({ status: "pending", summary: "" })));
      toast.success(`${data.count} sedute previste`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore calendario"); }
    finally { setBusy(false); }
  };

  const runChunk = async (idx, sess, chunkState) => {
    const slots = sess.slice(idx * CHUNK, (idx + 1) * CHUNK).map(({ date, time, duration, title }) => ({ date, time, duration, title }));
    const previous_summary = chunkState.slice(0, idx).filter((c) => c.status === "ok").map((c) => c.summary).slice(-2).join(" ");
    const { data } = await api.post("/trainings/bulk-plan/generate-chunk", {
      group_id: form.group_id, objective: form.objective, intensity_profile: form.intensity_profile,
      slots, chunk_index: idx, total_chunks: chunkState.length, previous_summary,
    });
    return data;
  };

  const generate = async (onlyMissing = false) => {
    if (!form.objective.trim()) return toast.error("Descrivi l'obiettivo generale");
    setBusy(true);
    let sess = [...sessions]; const cs = chunks.map((c) => ({ ...c }));
    let failed = 0;
    for (let i = 0; i < cs.length; i++) {
      if (onlyMissing && cs[i].status === "ok") continue;
      setProgress(`Blocco ${i + 1}/${cs.length}…`);
      try {
        const data = await runChunk(i, sess, cs);
        data.trainings.forEach((t, k) => { const pos = i * CHUNK + k; sess[pos] = { ...sess[pos], objective: t.objective, intensity: t.intensity, blocks: t.blocks }; });
        cs[i] = { status: "ok", summary: data.summary };
      } catch (e) { cs[i] = { ...cs[i], status: "error" }; failed++; }
      setSessions([...sess]); setChunks([...cs]);
    }
    setProgress(""); setBusy(false);
    if (failed) toast.error(`${failed} blocchi non generati: usa "Riprendi generazione"`); else toast.success("Contenuti generati per tutte le sedute");
  };

  const regenerateOne = async (pos) => {
    setBusy(true);
    try {
      const ci = Math.floor(pos / CHUNK);
      const previous_summary = chunks.slice(0, ci).filter((c) => c.status === "ok").map((c) => c.summary).slice(-2).join(" ");
      const s = sessions[pos];
      const { data } = await api.post("/trainings/bulk-plan/generate-chunk", {
        group_id: form.group_id, objective: form.objective, intensity_profile: form.intensity_profile,
        slots: [{ date: s.date, time: s.time, duration: s.duration, title: s.title }], chunk_index: ci, total_chunks: chunks.length, previous_summary,
      });
      const t = data.trainings[0];
      setSessions((arr) => { const a = [...arr]; a[pos] = { ...a[pos], objective: t.objective, intensity: t.intensity, blocks: t.blocks }; return a; });
      toast.success("Seduta rigenerata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore AI"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    const kept = sessions.filter((s) => s.keep && !s.existing_id).map((s) => ({ group_id: form.group_id, date: s.date, time: s.time, duration: s.duration, objective: s.objective, intensity: s.intensity, blocks: s.blocks }));
    if (kept.length === 0) return toast.error("Nessuna seduta selezionata");
    setBusy(true);
    try {
      const { data } = await api.post("/trainings/bulk-plan/create", { trainings: kept });
      toast.success(`${data.created} sedute create${data.skipped ? ` · ${data.skipped} saltate (già esistenti)` : ""}`);
      setOpen(false); reset(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const generatedAll = chunks.length > 0 && chunks.every((c) => c.status === "ok");
  const hasErrors = chunks.some((c) => c.status === "error");
  const keptCount = sessions?.filter((s) => s.keep && !s.existing_id).length || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <div onClick={() => setOpen(true)}>
        <Button data-testid="bulk-program-btn" variant="outline" className="h-12"><CalendarRange className="w-4 h-4 mr-2" />Programmazione massiva</Button>
      </div>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="w-5 h-5 text-[#0052FF]" />Programmazione massiva del gruppo</DialogTitle>
          <DialogDescription>Il calendario (date, orari, titoli) è calcolato dal software. L'AI propone solo i contenuti. Nulla viene salvato finché non premi “Crea programmazione”.</DialogDescription>
        </DialogHeader>

        {!calendar && (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Gruppo</Label>
                <Select value={form.group_id || "none"} onValueChange={(v) => setForm({ ...form, group_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="bulk-group"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Seleziona —</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data inizio</Label><Input data-testid="bulk-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Data fine</Label><Input data-testid="bulk-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div>
              <Label>Giorni settimanali e orari</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mt-1">
                {days.map((d, i) => (
                  <div key={i} className={`border rounded-md p-2 space-y-1 ${d.on ? "border-black" : "border-zinc-200 opacity-60"}`}>
                    <label className="flex items-center gap-2 text-sm font-semibold"><input data-testid={`bulk-day-${i}`} type="checkbox" checked={d.on} onChange={(e) => setDays((a) => a.map((x, k) => k === i ? { ...x, on: e.target.checked } : x))} />{DAYS[i]}</label>
                    <Input data-testid={`bulk-time-${i}`} type="time" className="h-8 text-xs" value={d.time} disabled={!d.on} onChange={(e) => setDays((a) => a.map((x, k) => k === i ? { ...x, time: e.target.value } : x))} />
                    <div className="flex items-center gap-1"><Input type="number" className="h-8 text-xs font-mono" value={d.duration} disabled={!d.on} onChange={(e) => setDays((a) => a.map((x, k) => k === i ? { ...x, duration: e.target.value } : x))} /><span className="text-[10px] text-zinc-500">min</span></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Esclusioni (festività, chiusure) — date singole o intervalli</Label>
              <Textarea data-testid="bulk-exclusions" rows={2} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} placeholder={"01/11/2026, 08/12/2026\n23/12/2026 → 06/01/2027"} className="font-mono text-sm" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
              <div><Label>Obiettivo generale della programmazione</Label><Textarea data-testid="bulk-objective" rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} placeholder="Es: stagione base I Turno — fondamentali di gambe, tecnica di base e primi assalti guidati" /></div>
              <div>
                <Label>Profilo intensità</Label>
                <Select value={form.intensity_profile} onValueChange={(v) => setForm({ ...form, intensity_profile: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="progressivo">Progressivo</SelectItem><SelectItem value="costante">Costante</SelectItem><SelectItem value="picco">Picco + scarico pre-gara</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {calendar && sessions && (
          <div className="space-y-3 py-2">
            <div className="flex flex-wrap items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-md p-3 text-sm">
              <span className="font-heading text-2xl font-bold" data-testid="bulk-count">{calendar.count} sedute previste</span>
              <span className="text-zinc-500">{groupName} · {form.start_date} → {form.end_date} · {days.filter((d) => d.on).map((d) => `${DAYS[d.weekday]} ${d.time}`).join(", ")}</span>
              {calendar.excluded_days > 0 && <span className="text-zinc-500">· {calendar.excluded_days} giorni esclusi</span>}
              {calendar.existing_count > 0 && <span className="text-amber-700 font-semibold" data-testid="bulk-existing">· {calendar.existing_count} già esistenti (non verranno duplicate)</span>}
              {progress && <span className="ml-auto flex items-center gap-1 text-[#0052FF]" data-testid="bulk-progress"><Loader2 className="w-4 h-4 animate-spin" />{progress}</span>}
              {chunks.length > 0 && !progress && <span className="ml-auto text-xs font-mono text-zinc-500">AI: {chunks.filter((c) => c.status === "ok").length}/{chunks.length} blocchi</span>}
            </div>
            <BulkProgramPreview sessions={sessions} setSessions={setSessions} onRegenerate={regenerateOne} busy={busy} />
          </div>
        )}

        <DialogFooter>
          {!calendar ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="bulk-compute" onClick={computeCalendar} disabled={busy} className="bg-black hover:bg-zinc-800">{busy ? "Calcolo…" : <><CalendarRange className="w-4 h-4 mr-2" />Calcola calendario</>}</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} disabled={busy}>Indietro</Button>
              {!generatedAll && !hasErrors && <Button data-testid="bulk-generate" onClick={() => generate(false)} disabled={busy} className="bg-[#0052FF] hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Genera contenuti AI</Button>}
              {hasErrors && <Button data-testid="bulk-resume" onClick={() => generate(true)} disabled={busy} variant="outline"><RotateCcw className="w-4 h-4 mr-2" />Riprendi generazione</Button>}
              <Button data-testid="bulk-create" onClick={create} disabled={busy || keptCount === 0} className="bg-black hover:bg-zinc-800">Crea programmazione ({keptCount})</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
